<template>
  <div class="bodyContainer" style="display: flex; flex-direction: column; gap: 20px; padding: 20px 0; width: 1300px; margin: 0 auto;">
    
    <!-- Top Card: File Upload -->
    <el-card shadow="always" style="border-radius: 16px; border: none; padding: 10px;">
      <div style="background-color: #ecf5ff; border-left: 4px solid #409eff; color: #606266; padding: 12px 16px; border-radius: 4px; margin-bottom: 10px; font-size: 14px; line-height: 1.5; display: flex; align-items: flex-start;">
        <i class="el-icon-info" style="color: #409eff; margin-right: 10px; font-size: 18px; margin-top: 1px;"></i>
        <div>
          Please upload a valid <strong>Newick (.nwk, .txt, .tree)</strong> or <strong>Nexus</strong> format tree file to display the Phylogenetic tree. Only <strong>1</strong> file can be uploaded at a time.
          <br/><span style="color: #e6a23c;">💡 <strong>Tip:</strong> Right-click on the tree nodes or branches to access the built-in interactive menu (Edit subtree, Rooting, Mark, Delete, etc.).</span>
        </div>
      </div>
      
      <div style="background-color: #f0f9eb; border-left: 4px solid #67c23a; color: #606266; padding: 12px 16px; border-radius: 4px; margin-bottom: 20px; font-size: 14px; line-height: 1.5; display: flex; align-items: flex-start;">
        <i class="el-icon-guide" style="color: #67c23a; margin-right: 10px; font-size: 18px; margin-top: 1px;"></i>
        <div>
          Alternatively, please select genomes from the <strong>Mitogenomes</strong> page or select species and genes from the <strong>Genes</strong> page to generate a phylogenetic tree.
          <br/>
          <span style="font-size: 13px; color: #606266; margin-top: 5px; display: inline-block;">
            <i class="el-icon-magic-stick"></i> If no specific genes are provided, the tree is built using <strong>24 core genes</strong> by default: 
            <el-tooltip class="item" effect="light" placement="bottom">
              <div slot="content" style="max-width: 300px; font-size: 13px; line-height: 1.5;">
                atp1, atp4, atp6, atp8, atp9, ccmB, ccmC, ccmFc, ccmFn, cob, cox1, cox2, cox3, matR, mttB, nad1, nad2, nad3, nad4, nad4L, nad5, nad6, nad7, nad9
              </div>
              <span style="cursor: pointer; text-decoration: underline; color: #409eff; margin-left: 5px;">Hover to view list</span>
            </el-tooltip>
          </span>
        </div>
      </div>
      <div class="upload">
        <el-upload
          class="upload-demo"
          ref="upload"
          action="#"
          :http-request="uploadFile"
          :on-change="handleChange"
          :file-list="fileList"
          :limit="1"
          :on-exceed="handleExceed"
          accept=".nwk,.txt,.nexus,.nex,.tree,.tre,.treefile"
        >
          <el-button slot="trigger" size="small" type="primary" plain>
            Upload tree file
          </el-button>
          <div v-if="!show" style="display: inline-block;">
            <el-button
              style="margin-left: 10px"
              size="small"
              type="success"
              @click="submitUpload"
              plain
            >
              Draw tree
            </el-button>
          </div>
          <div v-if="show" style="display: inline-block; margin-left: 20px;">
            <el-button
              size="small"
              type="success"
              @click="clearData"
              plain
            >
              Clear
            </el-button>
          </div>
        </el-upload>
      </div>
    </el-card>

    <!-- Fake Card: Tree Controls -->
    <div 
      v-show="show" 
      style="background: #fff; border-radius: 16px 16px 0 0; padding: 12px 10px; box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05); border-bottom: 1px solid #ebeef5; display: flex; justify-content: space-between; align-items: center; margin: 0;"
    >
      <div style="display: flex; align-items: center;">
        <el-radio-group v-model="layout" size="mini" @change="initTree(true)">
          <el-radio-button label="tree">Linear</el-radio-button>
          <el-radio-button label="radial">Radial</el-radio-button>
        </el-radio-group>
        
        <el-radio-group v-model="branchStyle" size="mini" style="margin-left: 10px;" @change="initTree(true)">
          <el-radio-button label="phylogram">Phylogram</el-radio-button>
          <el-radio-button label="topology">Cladogram</el-radio-button>
          <el-radio-button label="aligned">Dendrogram</el-radio-button>
        </el-radio-group>

        <el-switch
          v-model="showBootstrap"
          active-text="Bootstrap Values"
          style="margin-left: 10px;"
          @change="initTree(false)"
        >
        </el-switch>

        <el-switch
          v-model="showNumbers"
          active-text="Branch Lengths"
          style="margin-left: 10px;"
          @change="initTree(false)"
        >
        </el-switch>
      </div>
      <div style="display: flex; align-items: center;">
        <el-button v-if="isFromOtherPage && alignmentData" type="warning" size="mini" icon="el-icon-download" @click="downloadAlignment" style="margin-right: 5px; padding: 7px 10px; font-size: 12px;">Aligned FASTA</el-button>
        <el-button v-if="isFromOtherPage" type="success" size="mini" icon="el-icon-download" @click="downloadTreeFile" style="margin-right: 5px; padding: 7px 10px; font-size: 12px;">Tree File</el-button>
        <el-button type="primary" size="mini" icon="el-icon-download" @click="downloadSVG" style="padding: 7px 10px; font-size: 12px;">SVG</el-button>
      </div>
    </div>

    <!-- Bottom Card: Tree Display -->
    <el-card 
      v-show="show" 
      v-loading="isRendering"
      element-loading-text="Rendering tree layout..."
      element-loading-background="#ffffff"
      shadow="never" 
      :body-style="{ padding: '5px 20px 20px 20px' }"
      style="border-radius: 0 0 16px 16px; border: none; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05); min-height: 500px; position: relative; margin: 0; margin-bottom: 20px;"
    >
      <div 
        id="dendrogram" 
        style="width: 100%; min-height: 500px;"
        @mouseover="handleMouseOver"
        @mousemove="handleMouseMove"
        @mouseout="handleMouseOut"
      ></div>
      
      <!-- Custom Tooltip for Hover Effect -->
      <div 
        v-show="tooltipVisible" 
        class="phylo-tooltip"
        :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
      >
        <i class="el-icon-data-line" style="margin-right: 5px;"></i>
        Value: <strong>{{ tooltipText }}</strong>
      </div>
    </el-card>

  </div>
</template>

<script>
import phyloscape from "phyloscape/lib/phyloscape.main.js";

export default {
  name: "PhylogeneticTree",
  data() {
    return {
      fileList: [],
      treeData: "",
      alignmentData: null,
      show: false, // Controls whether to show the tree card
      isRendering: false, // Full-card loading state
      uploadedFile: null,
      layout: "tree", // Layout mode: 'tree' (Linear) or 'radial'
      branchStyle: "aligned", // Branch style default to Dendrogram
      showBootstrap: false, // Controls whether bootstrap values are shown
      showNumbers: false, // Controls whether numerical branch lengths are shown
      // Tooltip state
      tooltipVisible: false,
      tooltipX: 0,
      tooltipY: 0,
      tooltipText: "",
      isFromOtherPage: false,
      hoveredNodeD3: null,
      menuObserver: null,
    };
  },
  beforeDestroy() {
    if (this.menuObserver) {
      this.menuObserver.disconnect();
    }
  },
  mounted() {
    this.initObserver();
    let treeData = this.$route.params.treeData || sessionStorage.getItem('treeData');
    if (treeData) {
      try {
        let parsed = JSON.parse(treeData);
        if (parsed.tree) {
          this.treeData = parsed.tree;
          this.alignmentData = parsed.alignment;
        } else {
          this.treeData = treeData;
        }
      } catch (e) {
        this.treeData = treeData;
      }
      this.show = true;
      this.isFromOtherPage = true;
      sessionStorage.removeItem('treeData');
      this.$nextTick(() => {
        this.initTree();
      });
    }
  },
  methods: {
    downloadAlignment() {
      if (!this.alignmentData) return;
      const blob = new Blob([this.alignmentData], { type: "text/plain;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "mafft_aligned_genes.fasta");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    initObserver() {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === 1 && node.classList && node.classList.contains('phy-menu')) {
              const ul = node.querySelector('ul');
              
                // Override download SVG in menu
                const downloadSvgBtn = ul.querySelector('.download-svg');
                if (downloadSvgBtn) {
                  const newSvgBtn = downloadSvgBtn.cloneNode(true);
                  newSvgBtn.onclick = (e) => {
                    e.stopPropagation();
                    this.downloadSVG();
                    node.remove();
                  };
                  downloadSvgBtn.replaceWith(newSvgBtn);
                }

                // Override download PNG in menu
                const downloadPngBtn = ul.querySelector('.download-png');
                if (downloadPngBtn) {
                  const newPngBtn = downloadPngBtn.cloneNode(true);
                  newPngBtn.onclick = (e) => {
                    e.stopPropagation();
                    this.downloadPNG();
                    node.remove();
                  };
                  downloadPngBtn.replaceWith(newPngBtn);
                }
              
              if (this.hoveredNodeD3) {
                // Capture the node at the time the menu opens
                const targetNodeToRoot = this.hoveredNodeD3;
                
                // User right-clicked on a node or branch!
                
                // If it's a draw menu (happens when clicking a branch, as phyloscape ignores them)
                if (ul && ul.innerHTML.includes('reset-tree')) {
                  ul.innerHTML = ''; // Clear background options
                }
                
                // Add Rooting option
                if (ul && !ul.innerHTML.includes('set-root')) {
                  const li = document.createElement('li');
                  li.className = 'item set-root';
                  li.innerHTML = 'Rooting';
                  li.style.color = '#e6a23c';
                  li.style.fontWeight = 'bold';
                  li.onclick = () => {
                    this.rerootAtHoveredNode(targetNodeToRoot);
                    node.remove(); // hide menu
                  };
                  ul.appendChild(li);
                }
              }
            }
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      this.menuObserver = observer;
    },
    rerootAtHoveredNode(targetNode) {
      if (!targetNode) return;
      const targetD3Node = targetNode;
      if (!targetD3Node.parent) {
        this.$message.info("Already at root.");
        return;
      }
      
      let rootD3Node = targetD3Node;
      while (rootD3Node.parent) {
        rootD3Node = rootD3Node.parent;
      }
      
      const nodeMap = new Map();
      function cloneTree(n, parentClone) {
        const clone = { ...n.data, children: [] };
        nodeMap.set(n, clone);
        clone.parent = parentClone;
        if (n.children) {
          for (const child of n.children) {
            clone.children.push(cloneTree(child, clone));
          }
        }
        return clone;
      }
      
      const rootClone = cloneTree(rootD3Node, null);
      const targetClone = nodeMap.get(targetD3Node);
      
      const newRoot = { name: '', children: [] };
      
      let curr = targetClone;
      let prev = newRoot;
      let originalLength = curr.length || 0;
      
      curr.length = originalLength / 2;
      
      if (curr.parent) {
        curr.parent.children = curr.parent.children.filter(c => c !== curr);
      }
      
      let nextNodeToAttach = curr.parent;
      let lengthToNext = originalLength / 2;
      
      newRoot.children.push(curr);
      
      while (nextNodeToAttach) {
         const nextParent = nextNodeToAttach.parent;
         
         if (nextParent) {
           nextParent.children = nextParent.children.filter(c => c !== nextNodeToAttach);
         }
         
         const tempLen = nextNodeToAttach.length || 0;
         nextNodeToAttach.length = lengthToNext;
         lengthToNext = tempLen;
         
         if (prev === newRoot) {
           newRoot.children.push(nextNodeToAttach);
         } else {
           prev.children.push(nextNodeToAttach);
         }
         
         prev = nextNodeToAttach;
         nextNodeToAttach = nextParent;
      }
      
      if (rootClone !== newRoot && rootClone.children.length === 1) {
         const singleChild = rootClone.children[0];
         const parentArr = prev.children; 
         const idx = parentArr.indexOf(rootClone);
         if (idx !== -1) {
             parentArr.splice(idx, 1);
             singleChild.length = (singleChild.length || 0) + (rootClone.length || 0);
             parentArr.push(singleChild);
         }
      } else if (rootClone !== newRoot && rootClone.children.length === 0) {
         const parentArr = prev.children;
         const idx = parentArr.indexOf(rootClone);
         if (idx !== -1) parentArr.splice(idx, 1);
      }
      
      function toNewick(node) {
        let str = "";
        if (node.children && node.children.length > 0) {
          str += "(" + node.children.map(toNewick).join(",") + ")";
        }
        str += (node.name || "");
        if (node.length !== undefined && node.length !== null && !isNaN(node.length)) {
          str += ":" + node.length;
        }
        return str;
      }
      
      const newNewick = toNewick(newRoot) + ";";
      this.treeData = newNewick;
      this.initTree(true);
    },
    // Custom file upload handler to override default action
    uploadFile(options) {
      this.uploadedFile = options.file;
    },
    // Handle file selection
    handleChange(file, fileList) {
      this.fileList = fileList;
      if (file.status === 'ready') {
        this.uploadedFile = file.raw;
      }
    },
    // Exceed limit
    handleExceed() {
      this.$message.warning("Only 1 file can be uploaded. Please clear the existing file first.");
    },
    // Submit / Read file and Draw
    submitUpload() {
      if (!this.uploadedFile && this.fileList.length === 0) {
        this.$message.error("Please upload a tree file first.");
        return;
      }
      // If we don't have the raw file but we have it in fileList (fallback)
      const fileToRead = this.uploadedFile || (this.fileList[0] ? this.fileList[0].raw : null);
      if (!fileToRead) {
        this.$message.error("File is not ready.");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        if (!text || text.trim() === "") {
          this.$message.error("The uploaded file is empty.");
          return;
        }
        this.treeData = text;
        this.show = true;
        this.$nextTick(() => {
          this.initTree();
        });
      };
      reader.readAsText(fileToRead);
    },
    // Clear the drawing
    clearData() {
      this.fileList = [];
      this.uploadedFile = null;
      this.treeData = "";
      this.show = false;
      this.isFromOtherPage = false;
      // Also clear the dendrogram HTML content if any
      const container = document.getElementById("dendrogram");
      if (container) {
        container.innerHTML = "";
      }
    },
    // Initialize the PhyloScape tree
    initTree(showLoading = true) {
      const container = document.getElementById("dendrogram");
      if (container && this.treeData) {
        if (showLoading) {
          this.isRendering = true; // Show full card loading mask
          container.style.transition = 'none'; // Instant hide
          container.style.opacity = '0';
        }
        
        // Clear previous SVGs in case of re-rendering
        container.innerHTML = "";
        try {
          // Calculate available width, subtracting padding to leave space for labels on the right
          const availableWidth = container.offsetWidth - 350; 
          const dxVal = this.showNumbers ? 30 : 8;
          
          let treeSize;
          if (this.layout === 'radial') {
            // For radial trees, size is [angle, diameter-factor]. Radius = size[1] / 4.
            // To make diameter = availableWidth, we set size[1] to availableWidth * 1.8 (leaving some padding)
            treeSize = [360, availableWidth * 1.8];
            // Force container height to match the width to ensure the circle isn't cut off vertically
            container.style.height = availableWidth + 'px';
          } else {
            // For linear trees, size is [width, height]
            treeSize = [availableWidth, 0];
            container.style.height = 'auto';
          }

          // Process tree data based on selected mode
          let renderData = this.treeData;
          if (this.branchStyle === 'topology') {
            // Replace all branch lengths with ':1' so that horizontal distance is strictly proportional to topological depth
            renderData = this.treeData.replace(/:[-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?/g, ':1');
          }

          const config = {
            content: renderData,
            size: treeSize,
            separation: 45, // Increased vertical distance between nodes to prevent text overlapping
            
            // Dynamic Tree Style Configuration
            isPhylogram: this.branchStyle !== 'aligned',
            
            // Render leaf labels on the outermost edge and draw dashed lines from shorter nodes to maintain correspondence
            leafs: { show: true }, 
            extension: { 
              show: true,
              style: {
                stroke: "#b3c0d1", // Soft dashed line color
                "stroke-dasharray": "3,3",
                "stroke-width": 1
              }
            },

            nodes: { 
              // Colorize the node circles (points)
              symbol: {
                style: {
                  fill: "#409EFF", // Vibrant blue
                  stroke: "#1f69c0", // Darker blue border
                  "stroke-width": 1.5
                }
              },
              name: { 
                show: this.showBootstrap, 
                mark: "node", // Attach internal bootstrap values to nodes (leaves are handled by the outer 'leafs' system)
                dx: dxVal,
                dy: -8, // Shift up slightly above the branch line
                style: {
                  "font-size": "10px", // Smaller font
                  "fill": "#606266" // Neutral gray
                }
              },
              value: {
                show: this.showNumbers, 
                mark: "all",
                dx: dxVal,
                dy: 10, // Shift down to avoid overlapping with name
                style: {
                  "font-size": "10px", // Smaller font
                  "fill": "#e6a23c" // Warning orange for differentiation
                }
              }
            }
          };

          if (this.layout === 'radial') {
            new phyloscape.InitRadialStructure("#dendrogram", config);
          } else {
            new phyloscape.InitTreeStructure("#dendrogram", config);
          }
          
          // Wait for D3 animations to finish (up to 750ms), then run cleanup scripts
          setTimeout(() => {
            if (this.layout === 'radial') {
              this.fixUpsideDownLabels();
            }
            
            // Globally prevent text overlaps for internal nodes (crucial for dense radial trees)
            this.preventTextOverlap();
            
            if (showLoading) {
              container.style.transition = 'opacity 0.3s ease';
              container.style.opacity = '1';
              this.isRendering = false; 
            }
          }, 800);
        } catch (error) {
          console.error("PhyloScape initialization error:", error);
          this.isRendering = false;
          container.style.opacity = '1'; // Ensure it's visible on error
          this.$message.error("Failed to render the tree. The file format might be incorrect.");
        }
      }
    },
    // Flips inverted radial text for readability
    fixUpsideDownLabels() {
      const container = document.getElementById("dendrogram");
      if (!container) return;
      
      const texts = container.querySelectorAll(".radial text");
        texts.forEach(text => {
          const transform = text.getAttribute("transform") || "";
          const match = transform.match(/rotate\(([-0-9.]+)\)\s*translate\(([-0-9.]+)[, ]+([-0-9.]+)\)(?:\s*rotate\(([-0-9.]+)\))?/);
          
          if (match) {
            const A = parseFloat(match[1]);
            const R = parseFloat(match[2]);
            const Y = parseFloat(match[3]);
            let B = match[4] ? parseFloat(match[4]) : 0;
            
            let absAngle = ((A + B) % 360 + 360) % 360;
            
            if (absAngle > 90 && absAngle < 270) {
              B += 180;
              text.setAttribute("transform", `rotate(${A}) translate(${R}, ${Y}) rotate(${B})`);
              
              const currentAnchor = text.getAttribute("text-anchor") || "start";
              if (currentAnchor === "start") {
                text.setAttribute("text-anchor", "end");
                text.setAttribute("dx", "-5");
              } else {
                text.setAttribute("text-anchor", "start");
                text.setAttribute("dx", "5");
              }
            }
          }
        });
    },
    // Intelligent collision detection to hide overlapping internal node text
    preventTextOverlap() {
      const container = document.getElementById("dendrogram");
      if (!container) return;
      
      const nodeTexts = container.querySelectorAll(".node text");
      const boxes = [];
      
      nodeTexts.forEach(text => {
        text.style.display = "block"; // Reset to block before measuring
        const rect = text.getBoundingClientRect();
        
        // Ignore empty texts
        if (rect.width === 0 || rect.height === 0) return;

        // Add 2px padding
        const paddedRect = {
          left: rect.left - 2,
          right: rect.right + 2,
          top: rect.top - 2,
          bottom: rect.bottom + 2
        };

        let overlap = false;
        for (let i = 0; i < boxes.length; i++) {
          const b = boxes[i];
          if (!(paddedRect.right < b.left || 
                paddedRect.left > b.right || 
                paddedRect.bottom < b.top || 
                paddedRect.top > b.bottom)) {
            overlap = true;
            break;
          }
        }
        
        if (overlap) {
          text.style.display = "none";
        } else {
          boxes.push(paddedRect);
        }
      });
    },
    downloadTreeFile() {
      if (!this.treeData) return;
      const blob = new Blob([this.treeData], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "phylogenetic_tree.nwk";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    // Download SVG Handlers
    downloadSVG() {
      const container = document.getElementById("dendrogram");
      if (!container) return;
      
      const svg = container.querySelector("svg");
      if (!svg) {
        this.$message.error("No SVG graphic found to download.");
        return;
      }
      
      const clone = svg.cloneNode(true);
      if (!clone.getAttribute("xmlns")) {
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      }
      
      try {
        const bbox = svg.getBBox();
        const margin = 50;
        const vx = bbox.x - margin;
        const vy = bbox.y - margin;
        const vw = bbox.width + margin * 2;
        const vh = bbox.height + margin * 2;
        
        clone.setAttribute("viewBox", `${vx} ${vy} ${vw} ${vh}`);
        clone.setAttribute("width", vw);
        clone.setAttribute("height", vh);

        // Add white background so the downloaded SVG is not transparent
        const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        rect.setAttribute("x", vx);
        rect.setAttribute("y", vy);
        rect.setAttribute("width", vw);
        rect.setAttribute("height", vh);
        rect.setAttribute("fill", "#ffffff");
        clone.insertBefore(rect, clone.firstChild);
      } catch (e) {
        console.warn("Could not calculate bounding box for SVG:", e);
      }
      
      const svgData = new XMLSerializer().serializeToString(clone);
      const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "phylogenetic_tree.svg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    // Download PNG Handlers
    downloadPNG() {
      const container = document.getElementById("dendrogram");
      if (!container) return;
      
      const svg = container.querySelector("svg");
      if (!svg) {
        this.$message.error("No SVG graphic found to download.");
        return;
      }
      
      const clone = svg.cloneNode(true);
      if (!clone.getAttribute("xmlns")) {
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      }
      
      try {
        const bbox = svg.getBBox();
        const margin = 50;
        const vx = bbox.x - margin;
        const vy = bbox.y - margin;
        const vw = bbox.width + margin * 2;
        const vh = bbox.height + margin * 2;
        
        clone.setAttribute("viewBox", `${vx} ${vy} ${vw} ${vh}`);
        clone.setAttribute("width", vw);
        clone.setAttribute("height", vh);

        // Add white background so the downloaded PNG is not transparent
        const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        rect.setAttribute("x", vx);
        rect.setAttribute("y", vy);
        rect.setAttribute("width", vw);
        rect.setAttribute("height", vh);
        rect.setAttribute("fill", "#ffffff");
        clone.insertBefore(rect, clone.firstChild);
      } catch (e) {
        console.warn("Could not calculate bounding box for SVG:", e);
      }
      
      const svgData = new XMLSerializer().serializeToString(clone);
      const canvas = document.createElement("canvas");
      const img = new Image();
      
      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      
      img.onload = () => {
        // Use a scale factor for higher resolution export
        const scale = 2; 
        canvas.width = clone.getAttribute("width") * scale;
        canvas.height = clone.getAttribute("height") * scale;
        const ctx = canvas.getContext("2d");
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        
        const pngUrl = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = pngUrl;
        link.download = "phylogenetic_tree.png";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      };
      img.src = url;
    },
    // Tooltip Handlers
    handleMouseOver(event) {
      const target = event.target;
      let data = null;
      let d3Node = null;
      
      // If hovering over a branch path
      if (target.tagName.toLowerCase() === 'path' && target.closest('.links')) {
        d3Node = target.__data__ && target.__data__.target ? target.__data__.target : null;
        data = d3Node ? d3Node.data : null;
      } 
      // If hovering over a node circle or text
      else if (target.__data__ && target.__data__.data) {
        d3Node = target.__data__;
        data = d3Node.data;
      }

      this.hoveredNodeD3 = d3Node;

      if (data && data.length !== undefined && data.length !== null && data.length !== "") {
        this.tooltipText = data.length;
        // Position relative to the card container
        const cardRect = event.currentTarget.getBoundingClientRect();
        this.tooltipX = event.clientX - cardRect.left + 15;
        this.tooltipY = event.clientY - cardRect.top + 15;
        this.tooltipVisible = true;
      }
    },
    handleMouseMove(event) {
      if (this.tooltipVisible) {
        const cardRect = event.currentTarget.getBoundingClientRect();
        this.tooltipX = event.clientX - cardRect.left + 15;
        this.tooltipY = event.clientY - cardRect.top + 15;
      }
    },
    handleMouseOut() {
      this.tooltipVisible = false;
    }
  }
};
</script>

<style scoped>
/* Allow SVG nodes to render outside the strict bounding box to prevent half-cut circles */
::v-deep #dendrogram svg {
  overflow: visible !important;
}

/* Give the container some internal padding to accommodate the overflowing SVG nodes and shift it right */
#dendrogram {
  padding: 20px 50px;
  box-sizing: border-box;
}

/* Hide text entirely while processing radial labels to prevent visual jumping (FOUC) underneath the v-loading mask */
::v-deep #dendrogram.is-rendering-radial svg text {
  opacity: 0 !important;
}

/* Force text labels to be visible with clear contrast and size */
::v-deep #dendrogram svg text {
  fill: #303133;
  font-size: 12px !important;
  font-family: Arial, Helvetica, sans-serif !important;
  opacity: 1 !important;
  visibility: visible !important;
  display: block !important;
}

/* Tooltip Styles */
.phylo-tooltip {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.75);
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  pointer-events: none;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: opacity 0.1s;
  white-space: nowrap;
}
</style>
